import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Grid, Link, Stack, Typography, alpha } from '@mui/material';
import { ARTICLE_META } from 'const/blog';
import { BLOG_CATEGORIES, BlogCategory, categoryPath } from 'const/blog/categories';
import { PageUrl } from 'const/page-url';
import ArticleCard from './ArticleCard';
import BlogBreadcrumb from './BlogBreadcrumb';

// ==============================|| お役立ち記事の一覧 ||============================== //
//
// 全記事の一覧(/blog)と、カテゴリごとの一覧(/blog/category/<slug>)の両方をここで描く。
// category を渡すとそのカテゴリの記事だけに絞り、見出しと説明も差し替わる。
//
// 記事は const/blog/index.ts の ARTICLE_META の順にそのまま並べる。
// 日付でソートしていないのは、新着より「読ませたい順」を優先したいため
// (並べ替えたいときは ARTICLE_META の順番を入れ替える)。
//
// ★ このページは、プリレンダリング時に「記事とカテゴリの一覧を機械が読む場所」も兼ねている。
//   scripts/prerender.js は、ここに出力された /blog/... へのリンクを集めて
//   プリレンダリング対象と sitemap.xml を組み立てる。カードとカテゴリのリンクを
//   react-router の Link 以外に変えるとその仕組みが止まるので注意すること。
//
// ★ 記事が1本も無いカテゴリはリンクを出さない
//   中身の無いページへ誘導しないため。カテゴリを増やしても、記事を書くまでは表に出ない。

const primaryColor = '#2aa1af';

/** カテゴリの切り替えリンク。現在地は塗りつぶし、それ以外は枠線だけにする */
const CategoryTabs = ({ current }: { current?: BlogCategory }) => {
    const tabs = BLOG_CATEGORIES.filter((category) => ARTICLE_META.some((article) => article.category === category.label));
    if (tabs.length === 0) {
        return null;
    }

    const tabSx = (active: boolean) => ({
        px: { xs: 1.75, md: 2.25 },
        py: 0.9,
        borderRadius: 999,
        fontSize: { xs: '0.82rem', md: '0.86rem' },
        fontWeight: 700,
        lineHeight: 1.4,
        border: `1px solid ${alpha(primaryColor, active ? 1 : 0.35)}`,
        bgcolor: active ? primaryColor : '#fff',
        color: active ? '#fff' : '#0f3d43',
        '&:hover': { bgcolor: active ? primaryColor : alpha(primaryColor, 0.08) }
    });

    return (
        <Stack
            direction="row"
            spacing={{ xs: 1, md: 1.25 }}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: { xs: 4, md: 5 } }}
            aria-label="カテゴリ"
        >
            <Link component={RouterLink} to={PageUrl.Blog.urlWithSlash} underline="none" sx={tabSx(!current)}>
                すべて
            </Link>
            {tabs.map((category) => (
                <Link
                    key={category.slug}
                    component={RouterLink}
                    to={categoryPath(category.slug)}
                    underline="none"
                    sx={tabSx(current?.slug === category.slug)}
                >
                    {category.label}
                </Link>
            ))}
        </Stack>
    );
};

const BlogListTop = ({ category }: { category?: BlogCategory }) => {
    const articles = category ? ARTICLE_META.filter((article) => article.category === category.label) : ARTICLE_META;

    return (
        <Box
            component="section"
            sx={{
                pt: { xs: 4, md: 5 },
                pb: { xs: 8, md: 11 },
                background: `linear-gradient(180deg, ${alpha(primaryColor, 0.1)} 0%, ${alpha(primaryColor, 0.02)} 45%, #fff 100%)`
            }}
        >
            <Container maxWidth="md">
                <Box sx={{ mb: 3 }}>
                    <BlogBreadcrumb current={category?.label} />
                </Box>
                <Typography
                    component="span"
                    sx={{ display: 'block', color: primaryColor, fontWeight: 900, letterSpacing: '0.2em', fontSize: '0.78rem', mb: 1.5 }}
                >
                    COLUMN
                </Typography>
                <Typography
                    variant="h1"
                    sx={{ fontSize: { xs: '1.7rem', md: '2.2rem' }, fontWeight: 900, lineHeight: 1.5, color: '#0f3d43', mb: 2.5 }}
                >
                    {category ? category.heading : '児発・放デイの運営お役立ち記事'}
                </Typography>
                <Typography
                    component="p"
                    sx={{ color: '#4b5563', fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 2, mb: { xs: 4, md: 5 } }}
                >
                    {category
                        ? category.description
                        : '個別支援計画の書き方、加算の算定要件、報酬改定への対応など、児童発達支援管理責任者と管理者の方が実務で迷いやすいところを、記載例つきで整理しています。'}
                </Typography>

                <CategoryTabs current={category} />

                <Grid container spacing={{ xs: 2.5, md: 3 }}>
                    {articles.map((article) => (
                        <Grid item xs={12} sm={6} key={article.slug}>
                            <ArticleCard article={article} headingLevel="h2" />
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default BlogListTop;
